import React, { useEffect, useState } from 'react';
import Loader from '../components/Loader';
import Calling from '../api/Calling';
import { toast } from 'react-toastify';
import UserChart from '../components/charts/UserChart';
import LoginChart from '../components/charts/LoginChart';
import QuizChart from '../components/charts/QuizChart';
import WithdrawChart from '../components/charts/WithdrawChart';
import CoinDistributionCard from '../components/charts/CoinDistributionCard'; // Import the new component
import { FaRegUser } from 'react-icons/fa';
import { RiLoginBoxLine } from "react-icons/ri";
import { FiFilter, FiCalendar, FiRefreshCw } from "react-icons/fi";
import { GiQuicksand } from "react-icons/gi";
import { BiMoneyWithdraw } from "react-icons/bi";
import { MdDeleteForever } from "react-icons/md";
import { FaCoins } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import MainButton from '../components/ul/MainButton';


const Home = () => {
    const [loader, setLoader] = useState(false);
    const [userData, setUserData] = useState([]);
    const [loginData, setLogin] = useState([]);
    const [quizData, setQuizData] = useState([]);
    const [withdrawData, setWithdrawData] = useState([]);
    const [pendingWithdraws, setPendingWithdraws] = useState(0);
    const [totalWithdrawAmount, setTotalWithdrawAmount] = useState(0);
    const [totalDeleteRequests, setTotalDeleteRequests] = useState(0);
    const [totalCoins, setTotalCoins] = useState(0); // New state for total coins
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedBtn, setSelectedBtn] = useState('today');
    const [selectShowType, setSelectShowType] = useState('users'); // Changed default to 'users' to show everything by default
    const [dataFetchError, setDataFetchError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(new Date().toLocaleTimeString());
    const [withdrawTypeTotal, setWithdrawTypeTotal] = useState({});

    const navigate = useNavigate();

    const fetchData = async () => {
        setLoader(true);
        setDataFetchError(null);

        try {
            // Create data types based on the selected filter
            let dataTypes = selectShowType;

            // If "users" is selected, we'll fetch both users and login data
            if (selectShowType === 'users') {
                dataTypes = 'users,login,deleteRequests'; // Add deleteRequests to user data
            } else if (selectShowType === 'all') {
                dataTypes = 'users,login,totalQuizPlay,withDraw,deleteRequests'; // Add deleteRequests to all data
            } else if (selectShowType === 'withDraw') {
                dataTypes = 'withDraw';
            }

            const response = await Calling.adminDashbord(startDate, endDate, dataTypes);

            if (response?.status) {
                // Set data based on what's available in the response
                setUserData(response?.totalUser || []);
                setLogin(response?.totalLogin || []);
                setQuizData(response?.totalQuizPlay || []);
                setTotalDeleteRequests(response?.totalDeleteRequests || 0)
                setWithdrawData(response?.totalWithdraw || []);
                setPendingWithdraws(response?.totalWithdrawPending || 0);
                setTotalWithdrawAmount(response?.totalWithdrawAmountCompleted || 0);

                // Set the typeTotal data
                setWithdrawTypeTotal(response?.typeTotal || {});

                // Calculate total coins from quiz data
                if (response?.totalQuizPlay && response?.totalQuizPlay.length > 0) {
                    const coinSum = response.totalQuizPlay.reduce((sum, quiz) => {
                        const coinValue = quiz.coins || 0;
                        // For admin type, treat as deduction
                        if (quiz.type && quiz.type.toLowerCase() === 'admin') {
                            return sum - Math.abs(coinValue);
                        }
                        return sum + coinValue;
                    }, 0);
                    setTotalCoins(coinSum);
                } else {
                    setTotalCoins(0);
                }

                setLastUpdated(new Date().toLocaleTimeString());
            } else {
                toast.error(response?.message || 'Failed to fetch data');
                setDataFetchError(response?.message || 'Failed to fetch data');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to fetch dashboard data');
            setDataFetchError('Failed to fetch dashboard data');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [startDate, endDate, selectShowType]);

    const handleDateRangeFilter = (range) => {
        const today = new Date();
        let start, end;

        switch (range) {
            case 'today':
                start = today.toISOString().split('T')[0];
                end = start;
                setSelectedBtn('today')
                break;
            case 'yesterday':
                const yesterday = new Date();
                yesterday.setDate(today.getDate() - 1);
                start = yesterday.toISOString().split('T')[0];
                end = start;
                setSelectedBtn('yesterday');
                break;
            case 'thisWeek':
                const firstDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
                const lastDayOfWeek = new Date(firstDayOfWeek);
                lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
                start = firstDayOfWeek.toISOString().split('T')[0];
                end = lastDayOfWeek.toISOString().split('T')[0];
                setSelectedBtn('thisWeek')
                break;
            case 'thisMonth':
                start = new Date(today.getFullYear(), today.getMonth(), 1)
                    .toISOString()
                    .split('T')[0];
                end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
                    .toISOString()
                    .split('T')[0];
                setSelectedBtn('thisMonth')
                break;
            case 'last6Months':
                start = new Date(today.getFullYear(), today.getMonth() - 5, 1)
                    .toISOString()
                    .split('T')[0];
                end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
                    .toISOString()
                    .split('T')[0];
                setSelectedBtn('last6Months')
                break;
            case 'thisYear':
                start = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
                end = new Date(today.getFullYear(), 11, 31).toISOString().split('T')[0];
                setSelectedBtn('thisYear')
                break;
            case 'lastYear':
            default:
                start = '';
                end = '';
                setSelectedBtn('range')
        }

        setStartDate(start);
        setEndDate(end);
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchData();
    };

    const handleFormDate = (e) => {
        if (e.target.name === "startDate") {
            setStartDate(e.target.value);
            setSelectedBtn('range');
        } else {
            setEndDate(e.target.value);
            setSelectedBtn('range');
        }
    }

    const handleDataTypeChange = (type) => {
        setSelectShowType(type);
    };

    const handleRefresh = () => {
        fetchData();
    };

    // Component for data type filtering
    const DataTypeFilter = () => {
        return (
            <div className="p-6 bg-white border-t border-main-border">
                <h3 className="text-lg font-semibold text-main-font mb-4 flex items-center">
                    <FiFilter className="mr-2 text-main-blue" /> Data Type Filters
                </h3>
                <div className="flex flex-wrap gap-3">
                    <MainButton
                        onClick={() => handleDataTypeChange('all')}
                        label={'All Data'}
                        selectState={selectShowType === 'all'}
                    />
                    <MainButton
                        onClick={() => handleDataTypeChange('users')}
                        label={'Users'}
                        selectState={selectShowType === 'users'}
                    />
                    <MainButton
                        onClick={() => handleDataTypeChange('totalQuizPlay')}
                        label={'Quiz Data'}
                        selectState={selectShowType === 'totalQuizPlay'}
                    />
                    <MainButton
                        onClick={() => handleDataTypeChange('withDraw')}
                        label={'Redeem'}
                        selectState={selectShowType === 'withDraw'}
                    />
                </div>
            </div>
        );
    };

    // Function to render charts based on selected data
    const renderCharts = () => {
        // Determine what charts to show based on selected data type
        const showUserData = selectShowType === 'all' || selectShowType === 'users';
        const showQuizChart = selectShowType === 'all' || selectShowType === 'totalQuizPlay';
        const showWithdrawChart = selectShowType === 'all' || selectShowType === 'withDraw';

        return (
            <div className="space-y-6 mt-8">
                {/* First row - Main charts in a grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {showUserData && userData.length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm border border-main-border p-4 hover:shadow-md transition-all duration-300">
                            <UserChart userData={userData} />
                        </div>
                    )}
                    {showUserData && loginData.length > 0 && (
                        <div className="bg-white rounded-xl shadow-sm border border-main-border p-4 hover:shadow-md transition-all duration-300">
                            <LoginChart data={loginData} />
                        </div>
                    )}
                </div>

                {(showQuizChart || showWithdrawChart) && (
                    <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                        {showQuizChart && quizData.length > 0 && (
                            <div className="bg-white rounded-xl shadow-sm border border-main-border p-4 hover:shadow-md transition-all duration-300">
                                <QuizChart quizData={quizData} />
                            </div>
                        )}
                        {showWithdrawChart && withdrawData.length > 0 && (
                            <div className="bg-white rounded-xl shadow-sm border border-main-border p-4 hover:shadow-md transition-all duration-300">
                                <WithdrawChart
                                    withdrawData={withdrawData}
                                    typeTotal={withdrawTypeTotal}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Third row - Coin distribution card (always show when quiz data available) */}
                {quizData.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-main-border p-4 hover:shadow-md transition-all duration-300">
                        <CoinDistributionCard quizData={quizData} />
                    </div>
                )}
            </div>
        );
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Format number with commas
    const formatNumber = (num) => {
        return new Intl.NumberFormat().format(num);
    };

    // Function to render cards based on selected data
    const renderCards = () => {
        // Create an array of card configurations based on the current selection
        const cardConfigs = [
            // Combined Users card (always shows user data when 'users' or 'all' is selected)
            {
                show: selectShowType === 'all' || selectShowType === 'users',
                data: userData.length,
                label: 'Users',
                icon: <FaRegUser size={22} />,
                bgColor: 'bg-gradient-to-br from-teal-50 to-teal-100',
                borderColor: 'border-teal-200',
                iconBgColor: 'bg-teal-600',
                onClick: () => navigate('/manage/user/all/data')
            },
            // Login card (only shows when 'users' or 'all' is selected)
            {
                show: selectShowType === 'all' || selectShowType === 'users',
                data: loginData.length,
                label: 'Logins',
                icon: <RiLoginBoxLine size={24} />,
                bgColor: 'bg-gradient-to-br from-yellow-50 to-yellow-100',
                borderColor: 'border-yellow-200',
                iconBgColor: 'bg-yellow-500',
                onClick: () => navigate('/manage/user/login/all/data')
            },
            // Delete Requests card (always show regardless of filter when there are delete requests)
            {
                show: totalDeleteRequests > 0,
                data: totalDeleteRequests,
                label: 'Delete Requests',
                icon: <MdDeleteForever size={24} />,
                bgColor: 'bg-gradient-to-br from-red-50 to-red-100',
                borderColor: 'border-red-200',
                iconBgColor: 'bg-red-600',
                onClick: () => navigate('/manage/user/all/delete/req'),
                badge: totalDeleteRequests > 0 ? {
                    text: 'Action Required',
                    color: 'bg-red-600'
                } : null
            },
            // Quiz card
            {
                show: selectShowType === 'all' || selectShowType === 'totalQuizPlay',
                data: quizData.length,
                label: 'Quiz Plays',
                icon: <GiQuicksand size={24} />,
                bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100',
                borderColor: 'border-blue-200',
                iconBgColor: 'bg-blue-600',
                onClick: () => navigate('/manage/quiz/show')
            },
            // Total Coins card
            {
                show: (selectShowType === 'all' || selectShowType === 'totalQuizPlay') && quizData.length > 0,
                data: formatNumber(totalCoins),
                label: 'Total Coins',
                icon: <FaCoins size={20} />,
                bgColor: 'bg-gradient-to-br from-amber-50 to-amber-100',
                borderColor: 'border-amber-200',
                iconBgColor: 'bg-amber-500',
                onClick: () => navigate('/manage/quiz/show')
            },
            // Pending Withdrawals
            {
                show: selectShowType === 'all' || selectShowType === 'withDraw',
                data: pendingWithdraws,
                label: 'Pending Redeem',
                icon: <BiMoneyWithdraw size={24} />,
                bgColor: 'bg-gradient-to-br from-orange-50 to-orange-100',
                borderColor: 'border-orange-200',
                iconBgColor: 'bg-orange-500',
                onClick: () => navigate('/manage/withdraw/request'),
                badge: pendingWithdraws > 0 ? {
                    text: 'Action Needed',
                    color: 'bg-orange-500'
                } : null
            },
            // Total Withdrawal Amount
            {
                show: selectShowType === 'all' || selectShowType === 'withDraw',
                data: formatCurrency(totalWithdrawAmount),
                label: 'Total Paid',
                icon: <BiMoneyWithdraw size={24} />,
                bgColor: 'bg-gradient-to-br from-green-50 to-green-100',
                borderColor: 'border-green-200',
                iconBgColor: 'bg-green-600',
                onClick: () => navigate('/manage/withdraw/request')
            },
        ];

        // Filter out cards that shouldn't be shown
        const visibleCards = cardConfigs.filter(card => card.show);

        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 my-2">
                {visibleCards.map((card, index) => (
                    <div
                        key={index}
                        className={`flex flex-col break-words rounded-xl shadow-sm border ${card.borderColor} ${card.bgColor} hover:shadow-md transform hover:-translate-y-1 transition-all duration-300 cursor-pointer relative`}
                        onClick={card.onClick}
                    >
                        {/* Badge for cards that need attention */}
                        {card.badge && (
                            <div className="absolute -top-2 -right-2">
                                <span className={`${card.badge.color} text-white text-xs font-bold px-2 py-1 rounded-full shadow-md animate-pulse`}>
                                    {card.badge.text}
                                </span>
                            </div>
                        )}

                        <div className="flex justify-between items-center p-5">
                            <div>
                                <p className="text-sm font-semibold capitalize text-main-subfont">{card.label}</p>
                                <h4 className="text-2xl font-bold tracking-tight text-main-font mt-1">
                                    {card.data}
                                </h4>
                            </div>
                            <div className={`text-white ${card.iconBgColor} p-3 rounded-xl shadow-md`}>
                                {card.icon}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className='w-full text-main-font mt-16 px-2 md:px-3 md:ml-[4.4rem]'>
                    <div className="flex items-center justify-between pt-6 pb-2">
                        <h1 className="text-2xl font-bold text-main-font">Dashboard</h1>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleRefresh}
                                className="bg-main-blue hover:bg-opacity-90 text-white p-2 rounded-full shadow-sm transition-colors duration-200"
                                title="Refresh data"
                            >
                                <FiRefreshCw size={18} />
                            </button>
                            <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-main-border text-sm text-main-subfont flex items-center">
                                <span className="mr-2">Last updated:</span>
                                <span className="font-medium text-main-font">{lastUpdated}</span>
                            </div>
                        </div>
                    </div>

                    {/* Date Range Filters */}
                    <div className="bg-white rounded-xl shadow-sm border border-main-border overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center mb-4">
                                <FiCalendar className="text-main-blue mr-2" />
                                <h2 className="text-lg font-semibold text-main-font">Date Range</h2>
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-8 flex flex-wrap gap-3">
                                    <MainButton
                                        onClick={() => handleDateRangeFilter('today')}
                                        label={'Today'}
                                        selectState={selectedBtn === "today"}
                                    />
                                    <MainButton
                                        onClick={() => handleDateRangeFilter('yesterday')}
                                        label={'Yesterday'}
                                        selectState={selectedBtn === "yesterday"}
                                    />
                                    <MainButton
                                        onClick={() => handleDateRangeFilter('thisWeek')}
                                        label={'This Week'}
                                        selectState={selectedBtn === "thisWeek"}
                                    />
                                    <MainButton
                                        onClick={() => handleDateRangeFilter('thisMonth')}
                                        label={'This Month'}
                                        selectState={selectedBtn === "thisMonth"}
                                    />
                                    <MainButton
                                        onClick={() => handleDateRangeFilter('last6Months')}
                                        label={'Last 6 Months'}
                                        selectState={selectedBtn === "last6Months"}
                                    />
                                    <MainButton
                                        onClick={() => handleDateRangeFilter('thisYear')}
                                        label={'This Year'}
                                        selectState={selectedBtn === "thisYear"}
                                    />
                                </div>

                                {/* Date Range Form */}
                                <div className="col-span-12 md:col-span-4 flex justify-end">
                                    <form onSubmit={handleFilterSubmit} className="w-full md:w-auto">
                                        <div className="grid grid-cols-12 gap-3 justify-items-end">
                                            <div className="col-span-6 relative">
                                                <input
                                                    type="date"
                                                    name="startDate"
                                                    value={startDate}
                                                    onChange={handleFormDate}
                                                    min="2023-01-01"
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white ${selectedBtn === "range"
                                                        ? 'border-main-blue ring-2 ring-main-th text-main-blue'
                                                        : 'border-main-border text-main-font hover:border-main-blue'
                                                        }`}
                                                />
                                            </div>
                                            <div className="col-span-6 relative">
                                                <input
                                                    type="date"
                                                    name="endDate"
                                                    value={endDate}
                                                    onChange={handleFormDate}
                                                    min="2023-01-01"
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white ${selectedBtn === "range"
                                                        ? 'border-main-blue ring-2 ring-main-th text-main-blue'
                                                        : 'border-main-border text-main-font hover:border-main-blue'
                                                        }`}
                                                />
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        {/* Data Type Filter */}
                        <DataTypeFilter />
                    </div>

                    {/* Dashboard Content */}
                    <div className='p-4 bg-main-bg rounded-xl my-2 shadow-sm'>
                        {dataFetchError ? (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow-sm mb-6">
                                <div className="flex items-center">
                                    <svg className="w-6 h-6 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                    <span className="text-sm font-medium">{dataFetchError}</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Cards Section */}
                                {renderCards()}

                                {/* Charts Section */}
                                {renderCharts()}
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default Home;


